import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Theme, useTheme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import {
  Avatar,
  FormControl,
  List,
  ListItem,
  MenuItem,
  Select,
  Switch,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { RangeSlider, SingleSlider } from '../sliders';
import { useGlobalSettings, useViewer } from '../../hooks';
import LocalScene from '../../viewer/projections';
import { useDispatch, useSelector } from 'react-redux';
import { UnitConverter } from '../../viewer/utilities';
import {
  ColorMapKey,
  ColorType,
  MarkerNavType,
  changeAllClassificationVisibility,
  changeArrowMarkers,
  changeAvailableClassifications,
  changeCircularPoints,
  changeClassificationVisibility,
  changeCloudDistance,
  changeCloudMaxPoints,
  changeCloudMinimum,
  changeCloudScale,
  changeColorMap,
  changeColorType,
  changeDynamicSize,
  changeHeightClip,
  changeIntensityClip,
  changeModelDistance,
  changeOpacity,
  changeTagDistance,
  getAvailableClassifications,
  getClassificationVisibilities,
  selectArrowMarkers,
  selectCircularPoints,
  selectClassifications,
  selectCloudDistance,
  selectCloudMaxPoints,
  selectCloudMinimum,
  selectCloudScale,
  selectColorMap,
  selectColorType,
  selectDynamicSize,
  selectHeightClip,
  selectIntensityClip,
  selectModelDistance,
  selectOpacity,
  selectTagDistance
} from '../../redux/settings-slice';
import { getClassificationColors } from '../../classifications';
import { ArrowTooltip, SwitchWithLabel } from '../../components';
import {isEqual} from 'lodash';
import { colorMaps, getColorMap } from '../../viewer/textures/colormaps';
import { Units } from '../../types/project';
import { changeSavedAerialState, selectSavedAerialState } from '../../redux/camera-slice';
import { aerialBasemaps, emptyBasemapID } from '../../types/basemaps';

const converter = new UnitConverter();

const classificationInterval = 200;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    colorPreview: {
      height: theme.spacing(3),
      width: theme.spacing(3),
      marginRight: theme.spacing(1),
    },
    colorMapImage: {
      objectFit: 'fill',
      transform: 'rotate(-90deg)'
    },
    listItem: {
      width: '100%',
      marginBottom: theme.spacing(0.5)
    },
    formControl: {
      width: '100%',
      marginBottom: theme.spacing(1)
    }
  })
);

interface SectionTitleProps {
  text: string;
}

export const SectionTitle = (props: SectionTitleProps) => {
  return (
    <ListItem>
      <Typography gutterBottom style={{
        display: "inline-block",
        fontWeight: 'bold',
        fontSize: '0.95em'
      }}>
        {props.text}
      </Typography>
    </ListItem>
  );
};

interface SectionDescriptionProps {
  text: string;
}

export const SectionDescription = (props: SectionDescriptionProps) => {
  return (
    <ListItem>
      <Typography variant="subtitle2" style={{
        fontSize: "0.75rem"
      }}>
        {props.text}
      </Typography>
    </ListItem>
  );
};

/** Debug tools */
export const GPUPickerDebug = () => {
  const classes = useStyles();
  const {viewer} = useViewer();
  const {t} = useTranslation();

  const [pickerDebug, setPickerDebug] = useState(false);

  const onChange = useCallback((_, checked) => {
    setPickerDebug(checked);
  }, []);

  useEffect(() => {
    viewer?.togglePickerDebug(pickerDebug);
  }, [viewer, pickerDebug]);

  return (
    <ListItem>
      <div className={classes.listItem}>
        <SwitchWithLabel
          label={t("user-settings.gpu-picker")}
          checked={pickerDebug}
          onChange={onChange}
        />
      </div>
    </ListItem>
  );
};

export const PointsRenderDebug = () => {
  const classes = useStyles();
  const {viewer} = useViewer();
  const {t} = useTranslation();

  const [renderPaused, setRenderPaused] = useState(false);

  const onChange = useCallback((_, checked) => {
    setRenderPaused(checked);
  }, []);

  useEffect(() => {
    viewer?.setPointsRenderPaused(renderPaused);
  }, [viewer, renderPaused]);

  return (
    <ListItem>
      <div className={classes.listItem}>
        <SwitchWithLabel
          label={t('user-settings.pause-points-rendering')}
          checked={renderPaused}
          onChange={onChange}
        />
      </div>
    </ListItem>
  );
};

export const BoudingBoxDebug = () => {
  const classes = useStyles();
  const {viewer} = useViewer();
  const {t} = useTranslation();

  const [boundsVisible, setBoundsVisible] = useState(false);

  const onChange = useCallback((_, checked) => {
    setBoundsVisible(checked);
  }, []);

  useEffect(() => {
    viewer?.setPointBoundsVisible(boundsVisible);
  }, [viewer, boundsVisible]);

  return (
    <ListItem>
      <div className={classes.listItem}>
        <SwitchWithLabel
          label={t('user-settings.visible-point-bounds')}
          checked={boundsVisible}
          onChange={onChange}
        />
      </div>
    </ListItem>
  );
};

/** Classifications */
export const ClassificationList = () => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const theme = useTheme();
  const {viewer} = useViewer();
  const {t} = useTranslation();

  const classifications = useSelector(selectClassifications);

  const [classificationColors, setClassificationColors]
    = useState(Array(256).fill('#ffffff'));

  const savedAvailableClassifications = useMemo(() => {
    return getAvailableClassifications(classifications);
  }, [classifications]);

  const savedClassificationVisibilities = useMemo(() => {
    return getClassificationVisibilities(classifications);
  }, [classifications]);

  useEffect(() => {
    const updateColorsList = async () => {
      const colors = await getClassificationColors();
      setClassificationColors(colors);
    };

    updateColorsList();
  }, []);

  useEffect(() => {
    viewer?.setVisibleClassifications(savedClassificationVisibilities);
  }, [viewer, savedClassificationVisibilities]);

  /** Handle classification updates */
  useEffect(() => {
    const interval = setInterval(() => {
      if (!viewer) return;

      const newClasses = viewer.getAvailableClassifications();
      newClasses.sort();

      const oldClasses = savedAvailableClassifications.map(x => x.id);
      oldClasses.sort();

      if (isEqual(newClasses, oldClasses)) return;
      dispatch(changeAvailableClassifications(newClasses));
    }, classificationInterval);

    return () => {
      clearInterval(interval);
    };
  }, [viewer, classifications]);

  const hasClassifications = savedAvailableClassifications.length > 0;

  return (
    <List>
      <ListItem>
        <div className={classes.listItem} style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}>
          <Typography
            gutterBottom
            variant="subtitle2"
            style={{
              display: "inline-block",
              fontWeight: 'bold',
              fontSize: '0.95em'
            }}
          >
            {t("user-settings.classifications")}
          </Typography>

          {savedAvailableClassifications.length > 0 && (
            <ArrowTooltip
              title={t("user-settings.toggle-all-classifications")}
              placement="top"
            >
              <Switch
                checked={savedAvailableClassifications.every(
                  (classification) => classification.visible
                )}
                onChange={(_, checked) => {
                  dispatch(changeAllClassificationVisibility(checked));
                }}
              />
            </ArrowTooltip>
          )}
        </div>
      </ListItem>


      {!hasClassifications && (
        <div>
          <ListItem>
            <Typography gutterBottom variant="subtitle2">
              {t("user-settings.no-points-loaded")}
            </Typography>
          </ListItem>
        </div>
      )}

      {hasClassifications && (
        savedAvailableClassifications.map(classification => (
          <ListItem key={classification.id}>
            <div className={classes.listItem}>
              <SwitchWithLabel
                label={`${classification.name} (${classification.id})`}
                startAdornment={
                  <span
                    style={{
                      backgroundColor: classificationColors[classification.id],
                      height: theme.spacing(1.25),
                      width: theme.spacing(1.25),
                      borderRadius: '50%',
                      borderColor: '#000000',
                      borderStyle: 'solid',
                      borderWidth: '1px',
                      marginRight: theme.spacing(1),
                      display: 'inline-block'
                    }}
                  />
                }
                checked={classification.visible}
                onChange={(_, checked) => {
                  dispatch(changeClassificationVisibility(classification.id));
                }}
              />
            </div>
          </ListItem>
        ))
      )}

    </List>
  );
};

/** Model components */
export const ModelDistance = () => {
  const classes = useStyles();
  const {viewer} = useViewer();
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const savedModelDistance = useSelector(selectModelDistance);
  const units = LocalScene.viewProjectionUnits;

  const onChange = useCallback((value) => {
    viewer.setModelDistance(value);
  }, [viewer]);

  const onFinish = useCallback((value) => {
    dispatch(changeModelDistance(value));
  }, []);

  const labelFunction = useCallback((value) => {
    const maxValue = converter.convert(999, Units.Meters, units);
    return (value > maxValue) ? "∞" : `${value}${units}`;
  }, [units]);

  useEffect(() => {
    viewer?.setModelDistance(savedModelDistance);
  }, [viewer, savedModelDistance]);

  return (
    <ListItem>
      <div className={classes.listItem}>
        <Typography gutterBottom variant="subtitle2">
          {t("user-settings.model_distance")}
        </Typography>

        <SingleSlider
          value={savedModelDistance}
          onChange={onChange}
          onFinish={onFinish}
          min={0}
          max={1000}
          step={5}
          units={units}
          endAdornment={units}
          labelFunction={labelFunction}
        />
      </div>
    </ListItem>
  );
};

/** Tag components */
export const TagDrawDistance = () => {
  const classes = useStyles();
  const {viewer} = useViewer();
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const savedTagDistance = useSelector(selectTagDistance);
  const units = LocalScene.viewProjectionUnits;

  const onChange = useCallback((value) => {
    viewer?.setTagDistance(value);
  }, [viewer]);

  const onFinish = useCallback((value) => {
    dispatch(changeTagDistance(value));
  }, []);

  const labelFunction = useCallback((value) => {
    const maxValue = converter.convert(999, Units.Meters, units);
    return (value > maxValue) ? "∞" : `${value}${units}`;
  }, [units]);

  useEffect(() => {
    viewer?.setTagDistance(savedTagDistance);
  }, [viewer, savedTagDistance]);

  return (
    <ListItem>
      <div className={classes.listItem}>
        <Typography gutterBottom variant="subtitle2">
          {t("user-settings.tag-distance")}
        </Typography>

        <SingleSlider
          value={savedTagDistance}
          onChange={onChange}
          onFinish={onFinish}
          min={0}
          max={1000}
          step={5}
          units={units}
          endAdornment={units}
          labelFunction={labelFunction}
        />
      </div>
    </ListItem>
  );
};

/** Point cloud components */
export const PointCloudDistance = () => {
  const classes = useStyles();
  const {viewer} = useViewer();
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const savedCloudDistance = useSelector(selectCloudDistance);
  const units = LocalScene.viewProjectionUnits;

  const onChange = useCallback((value) => {
    viewer?.setPointCloudDistance(value);
  }, [viewer]);

  const onFinish = useCallback((value) => {
    dispatch(changeCloudDistance(value));
  }, []);

  const labelFunction = useCallback((value) => {
    const maxValue = converter.convert(999, Units.Meters, units);
    return (value > maxValue) ? "∞" : `${value}${units}`;
  }, [units]);

  useEffect(() => {
    viewer?.setPointCloudDistance(savedCloudDistance);
  }, [viewer, savedCloudDistance]);

  return (
    <ListItem>
      <div className={classes.listItem}>
        <Typography gutterBottom variant="subtitle2">
          {t("user-settings.point-distance")}
        </Typography>

        <SingleSlider
          value={savedCloudDistance}
          onChange={onChange}
          onFinish={onFinish}
          min={0}
          max={1000}
          step={5}
          units={units}
          endAdornment={units}
          labelFunction={labelFunction}
        />
      </div>
    </ListItem>
  );
};

export const PointCloudScale = () => {
  const classes = useStyles();
  const {viewer} = useViewer();
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const savedCloudScale = useSelector(selectCloudScale);

  const onChange = useCallback((value) => {
    viewer?.setPointCloudScale(value);
  }, [viewer]);

  const onFinish = useCallback((value) => {
    dispatch(changeCloudScale(value));
  }, []);

  useEffect(() => {
    viewer?.setPointCloudScale(savedCloudScale);
  }, [viewer, savedCloudScale]);

  return (
    <ListItem>
      <div className={classes.listItem}>
        <Typography gutterBottom variant="subtitle2">
          {t("user-settings.point-size")}
        </Typography>

        <SingleSlider
          value={savedCloudScale}
          onChange={onChange}
          onFinish={onFinish}
          min={0}
          max={3}
          step={0.1}
        />
      </div>
    </ListItem>
  );
};

export const PointCloudSize = () => {
  const classes = useStyles();
  const {viewer} = useViewer();
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const savedCloudMinimum = useSelector(selectCloudMinimum);

  const onChange = useCallback((value) => {
    viewer?.setPointCloudMinimum(value);
  }, [viewer]);

  const onFinish = useCallback((value) => {
    dispatch(changeCloudMinimum(value));
  }, []);

  useEffect(() => {
    viewer?.setPointCloudMinimum(savedCloudMinimum);
  }, [viewer, savedCloudMinimum]);

  return (
    <ListItem>
      <div className={classes.listItem}>
        <Typography gutterBottom variant="subtitle2">
          {t('user-settings.minimum-point-size')}
        </Typography>

        <SingleSlider
          value={savedCloudMinimum}
          onChange={onChange}
          onFinish={onFinish}
          min={0}
          max={3}
          step={0.1}
        />
      </div>
    </ListItem>
  );
};

export const AdaptivePointSize = () => {
  const classes = useStyles();
  const {viewer} = useViewer();
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const savedDynamicSize = useSelector(selectDynamicSize);

  const onChange = useCallback((_, checked) => {
    dispatch(changeDynamicSize(checked));
  }, []);

  useEffect(() => {
    viewer?.setPointCloudDynamicSize(savedDynamicSize);
  }, [viewer, savedDynamicSize]);

  return (
    <ListItem>
      <div className={classes.listItem}>
        <SwitchWithLabel
          label={t("user-settings.dynamic-point-sizing")}
          tooltip={t("user-settings.not-available-las")}
          checked={savedDynamicSize}
          onChange={onChange}
        />
      </div>
    </ListItem>
  );
};

export const PointCloudShape = () => {
  const classes = useStyles();
  const {viewer} = useViewer();
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const savedCircularPoints = useSelector(selectCircularPoints);

  const onChange = useCallback((_, checked) => {
    dispatch(changeCircularPoints(checked));
  }, []);

  useEffect(() => {
    viewer?.setPointCloudCircularPoints(savedCircularPoints);
  }, [viewer, savedCircularPoints]);

  return (
    <ListItem>
      <div className={classes.listItem}>
        <SwitchWithLabel
          label={t("user-settings.circular-points")}
          checked={savedCircularPoints}
          onChange={onChange}
        />
      </div>
    </ListItem>
  );
};

export const IntensityRange = () => {
  const classes = useStyles();
  const {viewer} = useViewer();
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const savedIntensityClip = useSelector(selectIntensityClip);
  const savedColorType = useSelector(selectColorType);

  const onChange = useCallback((value) => {
    viewer?.setPointCloudIntensityClip(value);
  }, [viewer]);

  const onFinish = useCallback((value) => {
    dispatch(changeIntensityClip(value));
  }, []);

  useEffect(() => {
    viewer?.setPointCloudIntensityClip(savedIntensityClip);
  }, [viewer, savedIntensityClip]);

  const visible = savedColorType === ColorType.Intensity;

  return (
    <React.Fragment>
      {visible && <ListItem>
        <div className={classes.listItem}>
          <Typography gutterBottom variant="subtitle2">
            {t("user-settings.intensity-range")}
          </Typography>

          <RangeSlider
            value={savedIntensityClip}
            onChange={onChange}
            onFinish={onFinish}
            min={-0.5}
            max={1.5}
            step={0.01}
            magnitude={100}
            endAdornment={"%"}
          />
        </div>
      </ListItem>
      }
    </React.Fragment>
  );
};

export const HeightRange = () => {
  const classes = useStyles();
  const {viewer} = useViewer();
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const savedHeightClip = useSelector(selectHeightClip);
  const savedColorType = useSelector(selectColorType);

  const onChange = useCallback((value) => {
    viewer?.setPointCloudHeightClip(value);
  }, [viewer]);

  const onFinish = useCallback((value) => {
    dispatch(changeHeightClip(value));
  }, []);

  useEffect(() => {
    viewer?.setPointCloudHeightClip(savedHeightClip);
  }, [viewer, savedHeightClip]);

  const visible = savedColorType === ColorType.Height;

  return (
    <React.Fragment>
      {visible && <ListItem>
        <div className={classes.listItem}>
          <Typography gutterBottom variant="subtitle2">
            {t("user-settings.height-range")}
          </Typography>

          <RangeSlider
            value={savedHeightClip}
            onChange={onChange}
            onFinish={onFinish}
            min={-0.5}
            max={1.5}
            step={0.01}
            magnitude={100}
            endAdornment={"%"}
          />
        </div>
      </ListItem>}
    </React.Fragment>
  );
};

export const ColorMapPicker = () => {
  const classes = useStyles();
  const {viewer} = useViewer();
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const savedColorMap = useSelector(selectColorMap);
  const savedColorType = useSelector(selectColorType);

  const onRender = useCallback((value) => {
    return getColorMap(value).name;
  }, []);

  const onChange = useCallback((event) => {
    const value = event.target.value as ColorMapKey;
    dispatch(changeColorMap(value));
  }, []);

  const getMenuItems = () => {
    return colorMaps.map((colorMap) => (
      <MenuItem key={colorMap.id} value={colorMap.id}>
        <Avatar
          classes={{
            img: classes.colorMapImage
          }}
          src={colorMap.image}
          className={classes.colorPreview}
        />
        {colorMap.name}
      </MenuItem>
    ));
  };

  useEffect(() => {
    viewer?.setPointCloudColorMap(savedColorMap);
  }, [viewer, savedColorMap]);

  const visible = (savedColorType === ColorType.Intensity)
    ||  (savedColorType === ColorType.Height);

  const options = getMenuItems();

  return (
    <React.Fragment>
      {visible && <ListItem>
        <div className={classes.listItem}>
          <Typography gutterBottom variant="subtitle2">
            {t("user-settings.color-map")}
          </Typography>

          <FormControl className={classes.formControl}>
            <Select
              value={savedColorMap}
              renderValue={onRender}
              onChange={onChange}
            >
              {options}
            </Select>
          </FormControl>
        </div>
      </ListItem>}
    </React.Fragment>
  );
};

export const PointCloudOpacity = () => {
  const classes = useStyles();
  const {viewer} = useViewer();
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const savedOpacity = useSelector(selectOpacity);

  const onChange = useCallback((value) => {
    viewer?.setPointCloudOpacity(value);
  }, [viewer]);

  const onFinish = useCallback((value) => {
    dispatch(changeOpacity(value));
  }, []);

  useEffect(() => {
    viewer?.setPointCloudOpacity(savedOpacity);
  }, [viewer, savedOpacity]);

  return (
    <ListItem>
      <div className={classes.listItem}>
        <Typography gutterBottom variant="subtitle2">
          {t("user-settings.point-visibility")}
        </Typography>

        <SingleSlider
          value={savedOpacity}
          onChange={onChange}
          onFinish={onFinish}
          min={0}
          max={1}
          step={0.01}
          magnitude={100}
          endAdornment={"%"}
        />
      </div>
    </ListItem>
  );
};

export const PointCloudCount = () => {
  const classes = useStyles();
  const {viewer} = useViewer();
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const savedMaxPoints = useSelector(selectCloudMaxPoints);

  const onChange = useCallback((value) => {
    viewer?.setPointCloudMaxPoints(value);
  }, [viewer]);

  const onFinish = useCallback((value) => {
    dispatch(changeCloudMaxPoints(value));
  }, []);

  useEffect(() => {
    viewer?.setPointCloudMaxPoints(savedMaxPoints);
  }, [viewer, savedMaxPoints]);

  return (
    <ListItem>
      <div className={classes.listItem}>
        <Typography gutterBottom variant="subtitle2">
          {t("user-settings.max-visible-points")}
        </Typography>

        <SingleSlider
          value={savedMaxPoints}
          onChange={onChange}
          onFinish={onFinish}
          min={0}
          max={10}
          step={0.05}
          endAdornment={"M"}
        />
      </div>
    </ListItem>
  );
};

export const PointCloudColor = () => {
  const classes = useStyles();
  const {viewer} = useViewer();
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const savedColorType = useSelector(selectColorType);

  const onChange = useCallback((event) => {
    const value = event.target.value as ColorType;
    dispatch(changeColorType(value));
  }, []);

  useEffect(() => {
    viewer?.setPointCloudColorType(savedColorType);
  }, [viewer, savedColorType]);

  return (
    <ListItem>
      <div className={classes.listItem}>
        <Typography gutterBottom variant="subtitle2">
          {t("user-settings.color-type")}
        </Typography>

        <FormControl className={classes.formControl}>
          <Select
            value={savedColorType}
            onChange={onChange}
          >
            <MenuItem key={0} value={0}>
              {t("color-type.intensity")}
            </MenuItem>
            <MenuItem key={1} value={1}>
              {t("color-type.color")}
            </MenuItem>
            <MenuItem key={2} value={2}>
              {t("color-type.height")}
            </MenuItem>
            <MenuItem key={3} value={3}>
              {t("color-type.classification")}
            </MenuItem>
          </Select>
        </FormControl>
      </div>
    </ListItem>
  );
};

export const NavigationType = () => {
  const classes = useStyles();
  const {viewer} = useViewer();
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const savedArrowMarkers = useSelector(selectArrowMarkers);

  const onChange = useCallback((event) => {
    let value = event.target.value as MarkerNavType;
    dispatch(changeArrowMarkers(value));
  }, []);

  useEffect(() => {
    viewer?.setNavigationType(savedArrowMarkers);
  }, [viewer, savedArrowMarkers]);

  return (
    <ListItem>
      <div className={classes.listItem}>
        <Typography gutterBottom variant="subtitle2">
          {t("user-settings.navigation-type")}
        </Typography>

        <FormControl className={classes.formControl}>
          <Select
            value={savedArrowMarkers}
            onChange={onChange}
          >
            <MenuItem key={0} value={MarkerNavType.Arrows}>
              {t("navigation-type.arrows")}
            </MenuItem>
            <MenuItem key={1} value={MarkerNavType.Markers}>
              {t("navigation-type.markers")}
            </MenuItem>
            <MenuItem key={2} value={MarkerNavType.None}>
              {t("navigation-type.none")}
            </MenuItem>
          </Select>
        </FormControl>
      </div>
    </ListItem>
  );
};

export const AerialBasemapPicker = () => {
  const classes = useStyles();
  const {viewer} = useViewer();
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const {aerialBackground} = useGlobalSettings();
  const aerialState = useSelector(selectSavedAerialState);
  const savedBaseMap = aerialState.basemap || emptyBasemapID;

  const onRender = useCallback((value) => {
    const basemap = aerialBasemaps.find(basemap => basemap.id === value);
    return t(basemap.text);
  }, []);

  const onChange = useCallback((event) => {
    const basemap = event.target.value as string;
    const newState = {...aerialState, basemap};
    dispatch(changeSavedAerialState(newState));
  }, []);

  useEffect(() => {
    if (!viewer) return;
    viewer.setAerialBasemap(savedBaseMap);
  }, [viewer, savedBaseMap]);

  const getMenuItems = () => {
    if (!viewer) return [];

    return aerialBasemaps.map((basemap) => {
      const isEmpty = basemap.id === emptyBasemapID;

      return (
        <MenuItem key={basemap.id} value={basemap.id} >
          <Avatar
            classes={{
              img: classes.colorMapImage
            }}
            style={{
              background: isEmpty ? aerialBackground : "initial"
            }}
            src={basemap.src}
            className={classes.colorPreview}
          />
          {t(basemap.text)}
        </MenuItem>
      );
    });
  };

  const options = getMenuItems();

  return (
    <React.Fragment>
      <ListItem>
        <div className={classes.listItem}>
          <Typography gutterBottom variant="subtitle2">
            {t('user-settings.base_map')}
          </Typography>

          <FormControl className={classes.formControl}>
            <Select
              value={savedBaseMap}
              renderValue={onRender}
              onChange={onChange}
            >
              {options}
            </Select>
          </FormControl>
        </div>
      </ListItem>
    </React.Fragment>
  );
};